import { Alert, List, ListItem, ListItemProps, styled } from "@mui/material";
import sortBy from "lodash-es/sortBy";
import {
  ReactElement,
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { FormattedMessage } from "react-intl";

import {
  GetThreadQuery,
  GetAssignmentsQuery,
  MessageDirection,
} from "~frontend/__generated__/graphql";
import { palette } from "~frontend/theme";
import { setEquals } from "~frontend/util/setEquals";

import { useThreadsContext } from "../ThreadsProvider";
import IncomingMessage from "./IncomingMessage";
import MessageDetailDialog from "./MessageDetailDialog";
import OutgoingMessage from "./OutgoingMessage";
import ThreadAction from "./ThreadAction";

const StyledList = styled(List)({
  flex: "1 1 auto",
  overflowY: "auto",

  // height: 0 causes flexbox to calculate the height, and then overflowY: auto
  // will make it scrollable (https://stackoverflow.com/a/14964944)
  height: 0,
  display: "flex",

  // We want to have the messages "stick" to the bottom and start with the
  // message list scrolled all the way down. We can do this by using
  // column-reverse to lay out the messages bottom-to-top, and then below
  // we reverse() the message list so that the most recent message is at the
  // bottom
  flexDirection: "column-reverse",

  backgroundColor: palette.white,
});

const StyledListItem = styled(ListItem)(
  (props: ListItemProps & { align: "left" | "right" | "center" }) => ({
    maxWidth: "900px",
    margin: "0 auto",
    justifyContent: { left: "flex-start", right: "flex-end", center: "center" }[
      props.align
    ],
  })
);

type Thread =
  | GetThreadQuery["getThread"]
  | GetAssignmentsQuery["getAssignments"][number];
type ThreadEntry = Thread["messages"][0] | Thread["threadActions"][0];

function ThreadEntryComponent({
  threadEntry,
  setDetailMessageId,
  showCampaignContext,
}: {
  threadEntry: ThreadEntry;
  setDetailMessageId: (id: string) => void;
  showCampaignContext?: boolean;
}): ReactElement | null {
  switch (threadEntry.__typename) {
    case "ApplyCampaignTagsThreadAction":
    case "RemoveCampaignTagsThreadAction":
    case "EditCampaignContactNoteThreadAction":
    case "MarkedNeedsResponseThreadAction":
    case "MarkedNotNeedsResponseThreadAction":
    case "OptedInThreadAction":
    case "OptedOutThreadAction":
    case "EscalateThreadAction":
    case "DeescalateThreadAction":
    case "MoveEscalationThreadAction":
    case "MoveInboxThreadAction":
      return <ThreadAction key={threadEntry.id} action={threadEntry} />;
    case "ThreadMessage":
      if (threadEntry.direction === "INBOUND") {
        return (
          <IncomingMessage
            key={threadEntry.id}
            message={threadEntry}
            showCampaignContext={showCampaignContext}
            onClickInfoButton={() => setDetailMessageId(threadEntry.id)}
          />
        );
      }

      return (
        <OutgoingMessage
          key={threadEntry.id}
          message={threadEntry}
          showCampaignContext={showCampaignContext}
          onClickInfoButton={() => setDetailMessageId(threadEntry.id)}
        />
      );
    default:
      return null;
  }
}

export type ThreadMessageListRef = {
  scrollToSearchMatch: (params: {
    campaignIds?: string[] | null;
    texterIds?: string[] | null;
    searchText?: string | null;
  }) => void;
  scrollToLatest: () => void;
};

type Props = {
  thread: Thread;
  showCampaignContexts?: boolean;
};

export default forwardRef<ThreadMessageListRef, Props>(
  function ThreadMessageList(
    { thread, showCampaignContexts },
    ref
  ): ReactElement {
    const { contextCampaignId, listThreadsFilterAndSort } =
      useThreadsContext() ?? {
        contextCampaignId: null,
        listThreadsFilterAndSort: null,
      };

    const listRef = useRef<HTMLUListElement>(null);

    // Expose an API to scroll to the most recent message matching the current filters.
    // This is used imperatively when the user clicks a link to a specific campaign in
    // the "Contact History" tab (e.g. it needs to be separate from state, because the
    // user can click, then scroll around, then click on the same link again, and we want
    // to scroll back to the campaign again)
    const scrollToSearchMatch = useCallback(
      (
        {
          campaignIds,
          texterIds,
          searchText,
        }: {
          campaignIds?: string[] | null;
          texterIds?: string[] | null;
          searchText?: string | null;
        },
        { scrollBehavior = "smooth" }: { scrollBehavior?: ScrollBehavior } = {}
      ) => {
        const campaignIdSet = campaignIds?.length ? new Set(campaignIds) : null;
        const texterIdSet = texterIds?.length ? new Set(texterIds) : null;
        const matchingMessage = thread.messages.findLast(message => {
          if (campaignIdSet) {
            if (!message.campaignId) {
              return false;
            } else if (!campaignIdSet.has(message.campaignId)) {
              return false;
            }
          }

          if (texterIdSet) {
            if (!message.createdByUserId) {
              return false;
            } else if (!texterIdSet.has(message.createdByUserId)) {
              return false;
            }
          }

          if (
            searchText &&
            !message.text.toLowerCase().includes(searchText.toLowerCase())
          ) {
            return false;
          }

          return true;
        });
        if (matchingMessage) {
          listRef.current
            ?.querySelector(`[data-message-id="${matchingMessage.id}"]`)
            ?.scrollIntoView({
              behavior: scrollBehavior,
              block: "end",
            });
        }
      },
      [thread.messages]
    );

    // Expose an API to scroll to the bottom of the message list. This is used
    // imperatively when the user sends a new message
    const scrollToLatest = useCallback(() => {
      listRef.current?.scrollTo({
        // we scroll to the "top" of the element, because we're rendering
        // in reverse order, so the "top" is the most recent message
        top: 0,
        behavior: "smooth",
      });
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        scrollToSearchMatch,
        scrollToLatest,
      }),
      [scrollToSearchMatch, scrollToLatest]
    );

    // Additionally, we want to auto-scroll to the context campaign when that
    // changes (both when we first load the thread, and when we change the context)
    const lastContextCampaignId = useRef<string | null>(null);

    const search =
      listThreadsFilterAndSort?.searchType === "message"
        ? listThreadsFilterAndSort?.search
        : "";
    const lastSearch = useRef<string | null>(null);

    const campaignIds =
      listThreadsFilterAndSort?.campaignFilter.map(campaign => campaign.id) ??
      null;
    const lastCampaignIds = useRef<string[] | null>(null);

    const texterIds =
      listThreadsFilterAndSort?.texterFilter.map(texter => texter.id) ?? null;
    const lastTexterIds = useRef<string[] | null>(null);

    useEffect(() => {
      if (
        search !== lastSearch.current ||
        !setEquals(new Set(campaignIds), new Set(lastCampaignIds.current)) ||
        !setEquals(new Set(texterIds), new Set(lastTexterIds.current))
      ) {
        scrollToSearchMatch(
          { searchText: search, campaignIds, texterIds },
          { scrollBehavior: "instant" }
        );
        lastSearch.current = search;
        lastCampaignIds.current = campaignIds;
        lastTexterIds.current = texterIds;
      }
      if (contextCampaignId !== lastContextCampaignId.current) {
        if (contextCampaignId != null) {
          scrollToSearchMatch(
            { campaignIds: [contextCampaignId] },
            {
              scrollBehavior:
                lastContextCampaignId.current == null ? "instant" : "smooth",
            }
          );
        }
        lastContextCampaignId.current = contextCampaignId;
      }
    }, [
      search,
      campaignIds,
      texterIds,
      scrollToSearchMatch,
      contextCampaignId,
    ]);

    const [detailMessageId, setDetailMessageId] = useState<string | null>(null);
    const entries = (thread.messages as ThreadEntry[]).concat(
      showCampaignContexts ? (thread.threadActions as ThreadEntry[]) : []
    );
    const sortedEntries = sortBy(entries, entry => {
      switch (entry.__typename) {
        case "ThreadMessage":
          return entry.threadOrderTime;
        case "ApplyCampaignTagsThreadAction":
        case "RemoveCampaignTagsThreadAction":
        case "EditCampaignContactNoteThreadAction":
        case "MarkedNeedsResponseThreadAction":
        case "MarkedNotNeedsResponseThreadAction":
        case "OptedInThreadAction":
        case "OptedOutThreadAction":
        case "EscalateThreadAction":
        case "DeescalateThreadAction":
        case "MoveEscalationThreadAction":
        case "MoveInboxThreadAction":
          return entry.createdAt;
      }
    });

    return (
      <>
        <StyledList ref={listRef}>
          {thread.__typename === "Assignment" &&
          thread.mostRecentMessageIsFromOtherCampaign ? (
            <StyledListItem align="left">
              <Alert severity="warning" sx={{ maxWidth: "540px" }}>
                <FormattedMessage
                  defaultMessage="New messages have been sent to this contact on a different campaign. Contact your account admin if you are unable to find the right response."
                  description="Warning message when an assignment has newer messages from another campaign"
                />
              </Alert>
            </StyledListItem>
          ) : null}
          {sortedEntries
            .slice()
            .reverse()
            .map(messageOrAction => {
              let align: "left" | "right" | "center";
              if (messageOrAction.__typename === "ThreadMessage") {
                if (messageOrAction.direction === MessageDirection.Inbound) {
                  align = "left";
                } else {
                  align = "right";
                }
              } else {
                align = "left";
              }
              return (
                <StyledListItem
                  key={messageOrAction.id}
                  data-campaign-id={
                    messageOrAction.__typename === "ThreadMessage"
                      ? messageOrAction.campaignId
                      : ""
                  }
                  data-message-id={
                    messageOrAction.__typename === "ThreadMessage"
                      ? messageOrAction.id
                      : ""
                  }
                  align={align}
                >
                  <ThreadEntryComponent
                    threadEntry={messageOrAction}
                    setDetailMessageId={setDetailMessageId}
                    showCampaignContext={showCampaignContexts}
                  />
                </StyledListItem>
              );
            })}
        </StyledList>
        {detailMessageId && (
          <MessageDetailDialog
            message={thread.messages.find(msg => msg.id === detailMessageId)!}
            onClose={() => setDetailMessageId(null)}
          />
        )}
      </>
    );
  }
);
